import {
  ApolloError,
  TypedDocumentNode,
  WatchQueryFetchPolicy,
  useQuery,
} from "@apollo/client";
import FilterList from "@mui/icons-material/FilterList";
import {
  Box,
  Stack,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  GridApi,
  GridCallbackDetails,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridInitialState,
  GridRowHeightParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridSortModel,
  GridValidRowModel,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import { DataGrid, DataGridProps } from "components/DataGrid";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useGridSelection } from "hooks/useGridSelection";
import {
  FilterKey,
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { NoRowsOverlay } from "routes/Customer/Chemicals/Inventory/Facility/NoRowsOverlay";
import { CommandButton } from "./CommandButton";
import {
  FilterGroup,
  InputValues,
  parseOmnisearch,
  stringifyOmnisearch,
} from "./Omnisearch/FilterPopover";
import { DateSearchHelpTooltip } from "./Omnisearch/helpComponents/DateSearchHelpTooltip";
import {
  OmnisearchColumnFilterMenu,
  OmnisearchColumnPopover,
} from "./OmnisearchColumnPopover";
import { OmnisearchErrorDisplay } from "./OmnisearchErrorDisplay";
import { TierIIYearPicker } from "./TierIIYearPicker";

export const helpTooltipByField: { [field: string]: React.ComponentType } = {
  createdAt: DateSearchHelpTooltip,
  updatedAt: DateSearchHelpTooltip,
  measuredAt: DateSearchHelpTooltip,
};
export type GridApiRef = React.MutableRefObject<GridApi | null>;
export type DataGridRef = React.RefObject<HTMLDivElement | null>;
export type OmnisearchInputRef = React.RefObject<HTMLInputElement | null>;
export type OmnisearchResetButtonRef =
  React.RefObject<HTMLButtonElement | null>;
export type OmnisearchCommandButtonsContext = {
  search: string;
  rowSelectionModel: GridRowSelectionModel;
};

export type OmnisearchDataGridProps<
  TData,
  TVariables,
  TRow extends GridValidRowModel
> = {
  apiRef?: GridApiRef;
  dataGridRef?: React.RefObject<HTMLDivElement | null>;
  omnisearchInputRef?: OmnisearchInputRef;
  omnisearchResetButtonRef?: OmnisearchResetButtonRef;
  autoFocus?: boolean;
  /** key to be used as an identifier for persisted state in localStorage */
  persistenceKey: string;
  columns: OmnisearchGridColDef<TRow>[];
  rowHeight?: number | "auto";
  getRowHeight?: (params: GridRowHeightParams) => number | "auto";
  dataQuery: TypedDocumentNode<TData, TVariables>;
  dataQueryVariables?: TVariables;
  defaultSearch?: string;
  commandButtons?:
    | React.ReactNode[]
    | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);
  /** sx for the Box wrapping the component */
  sx?: SxProps<Theme>;
  gridSx?: SxProps<Theme>;
  omnisearchWrapperSx?: SxProps<Theme>;
  slots?: Partial<GridSlotsComponent>;
  /** Additional columns to exclude from the Omnisearch filter popover. ("actions" is excluded by default) */
  excludeFilterColumns?: GridColDef["field"][];
  additionalFilterColumns?: FilterKey[];
  initialFilters?: { field: string; value: string | boolean }[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  /** Message to display in the grid when there is no data for the default search (default "No results found.") */
  noDataMessage?: string | string[];
  /** Button text to display in the grid when there is no data for the default search (must be defined to display the button) */
  noDataButtonText?: string;
  /** Set grid's selected IDs. This can include IDs across pages. */
  selectedIds?: GridRowId[];
  /** onClick handler for the button displayed in the grid when there is no data for the default search */
  noDataOnButtonClick?: () => void;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  /**
   * Handler called when selection changed with row data.
   *
   * **Cross-page support**: This handler DOES track selections across multiple pages.
   * Selected rows from other pages are preserved when navigating between pages.
   *
   * **Important considerations**:
   * - Row data for items on other pages will be cached/stale (not refreshed)
   * - When pre-selecting rows via `selectedIds` that aren't on the current page,
   *   those rows won't appear in the callback until you navigate to their page
   * - For current page items, you always get fresh/live object references
   *
   * **When to use this handler**:
   * - Bulk selection features where you need row data
   * - Use cases that don't require initial off-page row selection
   * - When cached data for off-page items is acceptable
   *
   * **When to use `onSelectedIdsChanged` instead**:
   * - When you need to pre-select rows that may be off-page initially
   * - When you need guaranteed fresh data for all selected items
   * - When you want to fetch row data separately based on IDs
   */
  onSelectedRowsChanged?: (rows: TRow[]) => void;
  /** Handler called when selection changed. This can include IDs across pages
   * */
  onSelectedIdsChanged?: (ids: GridRowId[]) => void;
  isRowSelectable?: (params: GridRowParams<TRow>) => boolean;
  initialPageSize?: number;
  /**
   * Function to handle the datagrid's onRowClick event.
   * i.e.
   * ```
   * const handleRowClick = (params: GridRowParams) => {
   *  history.push(`/facility/${params.id}`);
   * };
   */
  onRowClick?: (
    params: GridRowParams<TRow>,
    event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
    details: GridCallbackDetails
  ) => void;
  /**
   * Function to handle the datagrid's onLoadingStateChange event.
   */
  onLoadingStateChange?: (loading: boolean) => void;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails<any>
  ) => void;
  onColumnWidthChange?: GridEventListener<"columnWidthChange">;
  initialSortModel?: GridSortModel;
  onSearchChanged?: (search: string) => void;
  /**
   * Apollo client fetch policy. Defaults to "cache-and-network"
   */
  fetchPolicy?: WatchQueryFetchPolicy;
  initialState?: GridInitialState;
  /**
   * Indicates if Omnisearch should use the url querystring. Set to false to use
   * an internal search variable (i.e. for a modal).
   */
  isURLDriven?: boolean;
  otherLoading?: boolean;
  /** When true, the omnisearch max width will always be 100%. `commandButtons`
   * may still be used */
  showManageColumns?: boolean;
  showFilters?: boolean;
  hideOmnisearch?: boolean;
  /** when true, provide extra padding around grid */
  withPadding?: boolean;
  /** (optional) skip running query if true. Defaults to false */
  skip?: boolean;
  getDetailPanelContent?: (params: GridRowParams<TRow>) => React.ReactNode;
  getDetailPanelHeight?: (params: GridRowParams<TRow>) => "auto" | number;
  getRowId?: (row: TRow) => GridRowId;
  shouldPoll?: (data: TData) => number | undefined;
  disableRowSelectionOnClick?: boolean;
  checkboxSelection?: boolean;
  /** When true, hides the "select all" checkbox in the header while keeping individual row checkboxes */
  disableSelectAll?: boolean;
  onQueryError?: (error: ApolloError) => void;
  onApolloLoadingStateChange?: (loading: boolean) => void;
  onPreviousDataStateChange?: (previousData: TData | undefined) => void;
  onDataStateChange?: (data: TData | undefined) => void;
  onQueryStateChange?: (state: {
    data: TData | undefined;
    loading: boolean;
  }) => void;
  getRowLink?: DataGridProps<TRow>["getRowLink"];
  /** Optional filter groups for organizing filters in the popover */
  filterGroups?: FilterGroup[];
  /** Optional key aliases for omnisearch (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /**
   * Optional tenantId for backend queries. Will default to tenantId from the URL if present. (e.g. for customer routes)
   * Staff routes will not have the tenantId in the URL
   * - Do not provide tenantId if you intentionally want unscoped filters, such as on the fulfillment grid
   * - Provide the tenantId when you are operating with records specific to a tenant, such as with payments or mail.
   */
  tenantId?: string;
  /** Optional key for the year picker. */
  yearPickerKey?: string;
  /** Optional default value for the year picker. */
  yearPickerDefaultValue?: string;
  /** Optional boolean to show the "All" option in the year picker. Selecting All will remove the year filter. */
  yearPickerShowAllOption?: boolean;
  /** Optional handler for when the year picker value changes. */
  onYearPickerChange?: (year: string | undefined) => void;
  pageSizeOptions?: DataGridProps<TRow>["pageSizeOptions"];
  hideFooter?: DataGridProps<TRow>["hideFooter"];
};

export function OmnisearchDataGrid<
  TData,
  TVariables,
  TRow extends GridValidRowModel
>({
  apiRef,
  dataGridRef,
  omnisearchInputRef,
  omnisearchResetButtonRef,
  autoFocus,
  persistenceKey,
  columns: columnsInput,
  rowHeight,
  getRowHeight,
  dataQuery,
  dataQueryVariables,
  defaultSearch = "",
  commandButtons,
  sx,
  omnisearchWrapperSx,
  gridSx,
  slots,
  excludeFilterColumns,
  additionalFilterColumns,
  initialFilters,
  columnVisibilityModel: columnVisibilityModelProp,
  noDataMessage = "No results found.",
  noDataButtonText,
  selectedIds,
  noDataOnButtonClick,
  getItems,
  getCount,
  onSelectedRowsChanged,
  onSelectedIdsChanged,
  isRowSelectable,
  onRowClick,
  onLoadingStateChange,
  onColumnVisibilityModelChange,
  onColumnWidthChange,
  onSearchChanged,
  initialSortModel,
  fetchPolicy = "cache-and-network",
  initialState,
  initialPageSize,
  isURLDriven = false,
  otherLoading = false,
  showManageColumns,
  showFilters = true,
  hideOmnisearch = false,
  withPadding = true,
  skip = false,
  getDetailPanelContent,
  getDetailPanelHeight,
  getRowId,
  shouldPoll,
  disableRowSelectionOnClick,
  checkboxSelection = false,
  disableSelectAll = false,
  onQueryError,
  onApolloLoadingStateChange,
  onPreviousDataStateChange,
  onDataStateChange,
  onQueryStateChange,
  getRowLink,
  filterGroups,
  keyAliases,
  tenantId,
  yearPickerKey,
  yearPickerDefaultValue,
  yearPickerShowAllOption,
  onYearPickerChange,
  pageSizeOptions,
  hideFooter,
}: OmnisearchDataGridProps<TData, TVariables, TRow>) {
  const theme = useTheme();
  const { user } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const shouldShowManageColumns = useMemo(() => {
    if (showManageColumns === undefined)
      return columnsInput.filter((c) => c.type !== "actions").length >= 5;
    return showManageColumns;
  }, [showManageColumns, columnsInput]);

  const { tenantId: tenantIdFromParams } = useParams<{ tenantId: string }>();
  const selectedTenantId = tenantId ?? tenantIdFromParams;

  const {
    columns,
    omnisearch,
    debouncedOmnisearch,
    paginationModel,
    sortModel,
    setOmnisearch,
    setPaginationModel,
    setColumnVisibilityModel,
    columnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    setSortModel,
    columnFilterKeys,
  } = useOmnisearchDatagrid({
    persistenceKey,
    columns: columnsInput,
    additionalFilters: additionalFilterColumns,
    excludeFilters: excludeFilterColumns,
    isURLDriven,
    initialSortModel,
    initialPageSize,
    initialFilters,
    initialColumnVisibilityModel: columnVisibilityModelProp,
    isMobile,
  });

  // Track previous page size to detect changes without causing re-renders
  const previousPageSizeRef = useRef(paginationModel.pageSize);

  // Reset to page 0 when page size changes
  useEffect(() => {
    if (paginationModel.pageSize !== previousPageSizeRef.current) {
      previousPageSizeRef.current = paginationModel.pageSize;
      // Only reset if we're not already on page 0
      if (paginationModel.page > 0) {
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      }
    }
  }, [paginationModel.pageSize, paginationModel.page, setPaginationModel]);

  const getSearchString = useCallback(
    (defaultSearch: string, omnisearch: string) => {
      return [defaultSearch, omnisearch].join(" ").trim();
    },
    []
  );

  const [search, setSearch] = useState(() => {
    return getSearchString(defaultSearch, omnisearch);
  });
  // Use this for any readers who don't want to block rerender
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    setSearch(getSearchString(defaultSearch, debouncedOmnisearch));
  }, [defaultSearch, debouncedOmnisearch, getSearchString]);

  useEffect(
    () => onSearchChanged && onSearchChanged(search),
    [search, onSearchChanged]
  );

  const {
    data,
    error,
    loading: dataLoading,
    previousData,
    startPolling,
    stopPolling,
  } = useQuery(dataQuery, {
    variables: {
      search,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: sortModel,
      ...dataQueryVariables,
    },
    fetchPolicy,
    skip,
    onError: onQueryError,
  });

  useEffect(() => {
    if (onApolloLoadingStateChange) onApolloLoadingStateChange(dataLoading);
  }, [dataLoading, onApolloLoadingStateChange]);

  useEffect(() => {
    if (onPreviousDataStateChange) onPreviousDataStateChange(previousData);
  }, [previousData, onPreviousDataStateChange]);

  useEffect(() => {
    if (onDataStateChange) onDataStateChange(data);
  }, [data, onDataStateChange]);

  useEffect(() => {
    if (onQueryStateChange) onQueryStateChange({ data, loading: dataLoading });
  }, [data, dataLoading, onQueryStateChange]);

  useEffect(() => {
    const pollInterval = data ? shouldPoll?.(data) : undefined;
    if (pollInterval) {
      startPolling(pollInterval);
    } else {
      stopPolling();
    }
  }, [startPolling, stopPolling, shouldPoll, data]);

  const [items, count] = useMemo(() => {
    if (error) return [[], 0];
    if (data) return [getItems(data), getCount(data)];
    if (dataLoading && previousData)
      return [getItems(previousData), getCount(previousData)];
    return [[], 0];
  }, [data, previousData, dataLoading, error, getItems, getCount]);

  const loading =
    dataLoading ||
    otherLoading ||
    search !== getSearchString(defaultSearch, omnisearch);

  useEffect(() => {
    if (onLoadingStateChange) onLoadingStateChange(loading);
  }, [loading, onLoadingStateChange]);

  const { rowSelectionModel, setRowSelectionModel } = useGridSelection<TRow>({
    selectedIds,
    items,
    onSelectedRowsChanged,
    onSelectedIdsChanged,
  });

  // Replace the error throw with graceful handling
  const errorMessage = useMemo(() => {
    if (error) {
      return Array.isArray(error.message)
        ? error.message.join(". ")
        : error.message || "An error occurred while fetching data";
    }
    return null;
  }, [error]);

  const clearOmnisearch = useCallback(() => {
    setOmnisearch("");
  }, [setOmnisearch]);

  const noRowsMessage = useMemo(() => {
    if (errorMessage) return errorMessage;
    return omnisearch ? "No results found." : noDataMessage;
  }, [noDataMessage, omnisearch, errorMessage]);

  const noRowsButtonText = useMemo(
    () => (omnisearch ? "Clear search" : noDataButtonText),
    [noDataButtonText, omnisearch]
  );

  const noRowsOnButtonClick = useMemo(
    () => (omnisearch ? clearOmnisearch : noDataOnButtonClick),
    [clearOmnisearch, noDataOnButtonClick, omnisearch]
  );

  const onApplyFilters = useCallback(
    (newOmnisearch: string) => {
      setOmnisearch(newOmnisearch);
      // We want to update the search immediately when new filters are applied
      setSearch(getSearchString(defaultSearch, newOmnisearch));
    },
    [defaultSearch, setOmnisearch, getSearchString]
  );

  const [columnListAnchorEl, setColumnListAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const showColumnList = Boolean(columnListAnchorEl);

  const [filterButtonAnchor, setFilterButtonAnchor] =
    useState<HTMLButtonElement | null>(null);

  const handleFilterButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setFilterButtonAnchor(event.currentTarget);
    },
    []
  );

  const handleCloseFilter = useCallback(() => {
    setFilterButtonAnchor(null);
  }, []);

  const handleColumnList = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setColumnListAnchorEl(event.currentTarget);
    },
    []
  );

  const handleCloseColumnList = useCallback(() => {
    setColumnListAnchorEl(null);
  }, []);

  const resetColumnWidth = useCallback(
    (field: string) => {
      const colDef = columnsInput.find((c) => c.field === field);

      if (!colDef) return;

      unsetColumnWidthModel(colDef.field);
    },
    [columnsInput, unsetColumnWidthModel]
  );

  const handleToggleColumn = useCallback(
    (field: string) => {
      const newModel = {
        ...columnVisibilityModel,
        [field]:
          field in columnVisibilityModel
            ? !columnVisibilityModel[field]
            : false,
      };

      if (!newModel[field]) resetColumnWidth(field);

      setColumnVisibilityModel(newModel);

      if (onColumnVisibilityModelChange && apiRef?.current)
        onColumnVisibilityModelChange(newModel, {
          reason: "columnVisibilityChanged",
          api: apiRef?.current,
        });
    },
    [
      onColumnVisibilityModelChange,
      setColumnVisibilityModel,
      resetColumnWidth,
      columnVisibilityModel,
      apiRef,
    ]
  );

  const columnVisibilityModelChanged = useCallback(
    (model: GridColumnVisibilityModel, details: GridCallbackDetails) => {
      setColumnVisibilityModel(model);

      Object.entries(model).forEach(([field, visible]) => {
        if (!visible) resetColumnWidth(field);
      });

      if (onColumnVisibilityModelChange)
        onColumnVisibilityModelChange(model, details);
    },
    [onColumnVisibilityModelChange, setColumnVisibilityModel, resetColumnWidth]
  );

  const columnWidthChanged: GridEventListener<"columnWidthChange"> =
    useCallback(
      (params, event, details) => {
        setColumnWidthModel(params);

        if (onColumnWidthChange) onColumnWidthChange(params, event, details);
      },
      [onColumnWidthChange, setColumnWidthModel]
    );

  const memoizedSlots: Partial<GridSlotsComponent> | undefined = useMemo(
    () => ({
      columnsManagement: () => (
        <OmnisearchColumnFilterMenu
          columns={columns.filter(
            (column) =>
              column.headerName !== "actions" && column.field !== "actions"
          )}
          handleToggleColumn={handleToggleColumn}
          selectedColumns={columnVisibilityModel}
          setSelectedColumns={(model) => {
            setColumnVisibilityModel(model);
          }}
        />
      ),
      noRowsOverlay: (props) =>
        error ? (
          <OmnisearchErrorDisplay
            error={error}
            onClear={() => {
              clearOmnisearch();
              setSortModel([]);
            }}
          />
        ) : (
          <NoRowsOverlay
            {...props}
            onButtonClick={noRowsOnButtonClick}
            message={noRowsMessage}
            buttonText={noRowsButtonText}
          />
        ),
      ...slots,
    }),
    [
      slots,
      noRowsOnButtonClick,
      noRowsMessage,
      noRowsButtonText,
      error,
      clearOmnisearch,
      handleToggleColumn,
      columnVisibilityModel,
      setColumnVisibilityModel,
      columns,
      setSortModel,
    ]
  );

  const yearPickerValue = useMemo<string | undefined>(() => {
    if (yearPickerKey) {
      const filters = parseOmnisearch(omnisearch, keyAliases);
      return (
        (filters[yearPickerKey] as string | undefined) ?? yearPickerDefaultValue
      );
    }
    return undefined;
  }, [yearPickerKey, yearPickerDefaultValue, omnisearch, keyAliases]);

  const onYearPickerValueChanged = useCallback(
    (value: string | undefined) => {
      if (yearPickerKey) {
        const prev = parseOmnisearch(omnisearch, keyAliases);
        const newFilters = {
          ...prev,
          [yearPickerKey as keyof typeof prev]: value,
        } as InputValues;
        setOmnisearch(stringifyOmnisearch(newFilters, keyAliases));
      }
      if (onYearPickerChange) onYearPickerChange(value);
    },
    [yearPickerKey, omnisearch, keyAliases, setOmnisearch, onYearPickerChange]
  );

  const renderedCommandButtons = useMemo(() => {
    if (!commandButtons) return [];

    if (Array.isArray(commandButtons)) {
      return commandButtons;
    }

    if (typeof commandButtons === "function") {
      return commandButtons({
        search: deferredSearch,
        rowSelectionModel,
      });
    }

    return [];
  }, [commandButtons, deferredSearch, rowSelectionModel]);

  if (!user) return <></>;
  return (
    <Box sx={{ px: withPadding ? 3 : 0 }}>
      <Stack
        sx={{
          marginBottom: !hideOmnisearch ? theme.spacing(3) : theme.spacing(2),
          gap: theme.spacing(1.5),
          ...omnisearchWrapperSx,
        }}
        direction="row"
        flexWrap="wrap"
        alignItems="center"
      >
        {!hideOmnisearch && (
          <Omnisearch
            inputRef={omnisearchInputRef}
            resetRef={omnisearchResetButtonRef}
            autoFocus={autoFocus}
            omnisearch={omnisearch}
            setOmnisearch={setOmnisearch}
            onApplyFilters={onApplyFilters}
            columnFilterKeys={columnFilterKeys}
            filterGroups={filterGroups}
            showFilterButtonInside={false}
            filterButtonAnchor={filterButtonAnchor}
            onFilterButtonClick={handleFilterButtonClick}
            onFilterClose={handleCloseFilter}
            persistenceKey={persistenceKey}
            keyAliases={keyAliases}
            tenantId={selectedTenantId}
            sx={{
              width: {
                xs: "100%",
                lg: "inherit",
              },
              maxWidth: "1022px",
              flexGrow: 1,
            }}
          />
        )}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: theme.spacing(1.5),
            flexWrap: "wrap",
          }}
        >
          {showFilters && columnFilterKeys && columnFilterKeys.length > 0 && (
            <CommandButton onClick={handleFilterButtonClick}>
              <FilterList sx={{ mr: 1 }} fontSize="small" />
              Filters
            </CommandButton>
          )}

          {yearPickerKey && (
            <TierIIYearPicker
              persistQueryParam={false}
              showAllOption={yearPickerShowAllOption}
              value={yearPickerValue}
              onChange={(value) =>
                onYearPickerValueChanged(value ? String(value) : undefined)
              }
            />
          )}

          {shouldShowManageColumns && (
            <>
              <CommandButton onClick={handleColumnList}>
                Manage Columns
              </CommandButton>
              {showColumnList && (
                <OmnisearchColumnPopover
                  showColumnList={showColumnList}
                  anchorEl={columnListAnchorEl}
                  handleCloseColumnList={handleCloseColumnList}
                  columns={columns.filter(
                    (column) =>
                      column.headerName !== "actions" &&
                      column.field !== "actions"
                  )}
                  handleToggleColumn={handleToggleColumn}
                  selectedColumns={columnVisibilityModel}
                  setSelectedColumns={(model) => {
                    setColumnVisibilityModel(model);
                  }}
                />
              )}
            </>
          )}
          {renderedCommandButtons.length > 0 && renderedCommandButtons}
        </Stack>
      </Stack>
      <Box position="relative" sx={sx}>
        <DataGrid
          apiRef={apiRef}
          innerRef={dataGridRef}
          columns={columns}
          rows={items}
          // Defaulted to match our current designs but allows it to be overridden
          getRowHeight={getRowHeight ?? (() => rowHeight ?? 38)}
          autoHeight={rowHeight === "auto"}
          getRowId={getRowId}
          sx={{
            scrollMarginTop: 10, // Help prevent scrolling past the top of the grid
            "& .MuiDataGrid-virtualScroller": {
              minHeight: items.length === 0 || error ? "400px" : "0",
            },
            opacity:
              loading && previousData
                ? theme.palette.action.disabledOpacity
                : 1,
            ...(disableSelectAll && {
              "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
                {
                  display: "none",
                },
            }),
            ...gridSx,
          }}
          checkboxSelection={
            !!onSelectedRowsChanged ||
            !!onSelectedIdsChanged ||
            checkboxSelection
          }
          disableRowSelectionOnClick={
            typeof disableRowSelectionOnClick === "boolean"
              ? disableRowSelectionOnClick
              : !(!!onSelectedRowsChanged || !!onSelectedIdsChanged)
          }
          loading={loading}
          rowCount={count}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newModel) => {
            setRowSelectionModel(newModel);
          }}
          isRowSelectable={isRowSelectable}
          onColumnVisibilityModelChange={columnVisibilityModelChanged}
          onColumnWidthChange={columnWidthChanged}
          keepNonExistentRowsSelected
          slots={memoizedSlots}
          onRowClick={onRowClick}
          columnVisibilityModel={columnVisibilityModel}
          initialState={initialState}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          hideFooterPagination={false}
          hideFooter={hideFooter}
          getRowLink={getRowLink}
          pageSizeOptions={pageSizeOptions}
        />
      </Box>
    </Box>
  );
}
